import {
  NovaListConversationsResponse,
  NovaGetConversationResponse,
  NovaDeleteConversationsResponse,
  NovaDocumentObject,
  NovaListDocumentsResponse,
  NovaDeleteDocumentResponse,
  NovaSuggestionsResponse,
  NovaPreferences,
  NovaPreferencesResponse,
  NovaSaveConversationRequest,
  NovaSaveConversationResponse,
  NovaChatMessage,
} from '@/types';
import { buildAPIUrl, responseInterceptor, toQueryString } from '.';
import {
  mockListNovaConversations,
  mockGetNovaConversation,
  mockDeleteNovaConversations,
  mockGetNovaSuggestions,
  mockDismissNovaSuggestion,
  mockGetNovaPreferences,
  mockSaveNovaPreferences,
} from './nova-data.mock';

// Conversation history, suggestions and preferences have no backend yet
// (/services/nova/* doesn't exist). Those calls stay on the in-memory mock until
// that backend is built. Set NEXT_PUBLIC_NOVA_MOCK_API=false once it's live.
// Chat itself is handled separately in api/nova-ava.ts against the real AVA stream.
const useMock = false; //process.env.NEXT_PUBLIC_NOVA_MOCK_API !== 'false';

const baseNovaUrl = 'services/gdai/nova';

export const listNovaConversations = async (aadGuid: string): Promise<NovaListConversationsResponse> => {
  if (useMock) return mockListNovaConversations();

  const resp = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/${baseNovaUrl}/listConversations?aadGuid=${aadGuid}`),
  );

  return resp.json();
};

export const getNovaConversation = async (
  aadGuid: string,
  conversationId: string,
): Promise<NovaGetConversationResponse> => {
  if (useMock) return mockGetNovaConversation(conversationId);

  const resp = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/${baseNovaUrl}/getConversation${toQueryString({ aadGuid, conversationId })}`),
  );

  return resp.json();
};

export const deleteNovaConversations = async (
  aadGuid: string,
  conversationIds: string[],
): Promise<NovaDeleteConversationsResponse> => {
  if (useMock) return mockDeleteNovaConversations(conversationIds);

  const resp = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/${baseNovaUrl}/deleteConversations?aadGuid=${aadGuid}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversationIds }),
    }),
  );

  return resp.json();
};

export const saveNovaConversation = async (
  aadGuid: string,
  chat: NovaSaveConversationRequest,
): Promise<NovaSaveConversationResponse> => {
  if (useMock) return { saved: true };

  const resp = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/${baseNovaUrl}/saveConversation?aadGuid=${aadGuid}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(chat),
    }),
  );

  return resp.json();
};

// Nova document library (services/gdai/nova/documents). A user's uploads live flat
// under `${NOVA_DOCUMENTS_KEY_PREFIX}/${userProfileId}/` — AVA reads them back from
// there, so keys must always be built with buildNovaDocumentKey.
export const NOVA_DOCUMENTS_KEY_PREFIX = 'Stylus-Ava-User-docs';

export const buildNovaDocumentKey = (userProfileId: string, filename: string) =>
  `${NOVA_DOCUMENTS_KEY_PREFIX}/${userProfileId}/${filename}`;

// The only document types the backend accepts, mapped to their file extensions.
// Browsers often report an empty (or non-standard, e.g. text/x-markdown) MIME
// type for .md and sometimes .json, so a file passes on either its MIME type or
// its extension.
const NOVA_ALLOWED_DOCUMENT_TYPES: Record<string, string[]> = {
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/json': ['.json'],
  'text/markdown': ['.md', '.markdown'],
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'text/plain': ['.txt'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
};

const NOVA_ALLOWED_DOCUMENT_EXTENSIONS = Object.values(NOVA_ALLOWED_DOCUMENT_TYPES).flat();

// For <input type="file" accept>.
export const NOVA_DOCUMENT_ACCEPT = [
  ...Object.keys(NOVA_ALLOWED_DOCUMENT_TYPES),
  ...NOVA_ALLOWED_DOCUMENT_EXTENSIONS,
].join(',');

// Per-file upload cap. Keep below next.config.ts's middlewareClientMaxBodySize —
// uploads are proxied through middleware, which truncates bodies past that size.
export const NOVA_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;
export const NOVA_IMAGE_MAX_BYTES = 2 * 1024 * 1024;
export const NOVA_DOCUMENT_MAX_FILES = 5;

// Human-readable list for hints and error messages.
export const NOVA_DOCUMENT_FORMATS_LABEL = 'PDF, DOC, DOCX, PPTX, TXT, MD, JSON, PNG, JPG, WEBP';
export const NOVA_DOCUMENT_LIMITS_LABEL = `Up to ${NOVA_DOCUMENT_MAX_FILES} files at a time · file up to 10 MB, image up to 2 MB each`;

const mimeTypeFromExtension = (filename: string): string | undefined => {
  const dot = filename.lastIndexOf('.');
  if (dot === -1) return undefined;
  const extension = filename.slice(dot).toLowerCase();
  return Object.keys(NOVA_ALLOWED_DOCUMENT_TYPES).find((mime) => NOVA_ALLOWED_DOCUMENT_TYPES[mime].includes(extension));
};

export const isAllowedNovaDocument = (file: File): boolean =>
  (!!file.type && file.type in NOVA_ALLOWED_DOCUMENT_TYPES) || !!mimeTypeFromExtension(file.name);

export const isNovaImage = (file: File): boolean =>
  (file.type || mimeTypeFromExtension(file.name) || '').startsWith('image/');

export const novaDocumentMaxBytes = (file: File): number =>
  isNovaImage(file) ? NOVA_IMAGE_MAX_BYTES : NOVA_DOCUMENT_MAX_BYTES;

// The backend returns a bare array of `{ key, size, lastModified }` (no name) —
// wrapped here into the `succeeded`/`documents` shape the Nova components consume,
// with `name` taken from the key's trailing filename segment.
export const listNovaDocuments = async (aadGuid: string, userProfileId: string): Promise<NovaListDocumentsResponse> => {
  const resp = await responseInterceptor(
    await fetch(
      `${await buildAPIUrl()}/${baseNovaUrl}/documents${toQueryString({
        aadGuid,
        keyPrefix: `${NOVA_DOCUMENTS_KEY_PREFIX}/${userProfileId}/`,
      })}`,
    ),
  );

  const items: { key?: string; size?: number; lastModified?: string }[] = (await resp.json()) ?? [];
  const documents: NovaDocumentObject[] = items
    .filter((item): item is { key: string; size?: number; lastModified?: string } => !!item?.key && !item.key.endsWith('/'))
    .map((item) => ({
      key: item.key,
      name: item.key.split('/').pop() || item.key,
      size: item.size ?? 0,
      lastModified: item.lastModified ?? new Date(0).toISOString(),
    }))
    .sort((a, b) => b.lastModified.localeCompare(a.lastModified));

  return { succeeded: true, documents };
};

// Multipart upload (form field `file`) to the exact object `key`. XHR rather than
// fetch so the upload dialog can show byte-level progress.
export const uploadNovaDocument = async (
  aadGuid: string,
  key: string,
  file: File,
  onProgress?: (bytesUploaded: number) => void,
): Promise<void> => {
  const url = `${await buildAPIUrl()}/${baseNovaUrl}/documents/upload${toQueryString({ aadGuid, key })}`;
  // The multipart part's Content-Type is the File's own `type` — empty (sent as
  // application/octet-stream) for .md and often .json on Windows, which the
  // backend's MIME allow-list rejects. Re-type those from the extension.
  const mimeType = file.type in NOVA_ALLOWED_DOCUMENT_TYPES ? file.type : mimeTypeFromExtension(file.name);
  const upload = mimeType && mimeType !== file.type ? new File([file], file.name, { type: mimeType }) : file;
  const formData = new FormData();
  formData.append('file', upload, file.name);

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded);
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed with status ${xhr.status}`));
    });
    xhr.addEventListener('error', () => reject(new Error('Upload failed')));
    xhr.open('POST', url);
    xhr.send(formData);
  });
};

export const deleteNovaDocument = async (aadGuid: string, key: string): Promise<NovaDeleteDocumentResponse> => {
  const resp = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/${baseNovaUrl}/documents/delete${toQueryString({ aadGuid, key })}`, {
      method: 'DELETE',
    }),
  );

  return { succeeded: resp.ok };
};

export const getNovaSuggestions = async (aadGuid: string): Promise<NovaSuggestionsResponse> => {
  if (useMock) return mockGetNovaSuggestions();

  const resp = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/${baseNovaUrl}/suggestions?aadGuid=${aadGuid}`),
  );
  return resp.json();
};

export const dismissNovaSuggestion = async (aadGuid: string, suggestionId: string): Promise<{ succeeded: boolean }> => {
  if (useMock) return mockDismissNovaSuggestion(suggestionId);

  const resp = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/${baseNovaUrl}/dismissSuggestion?aadGuid=${aadGuid}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ suggestionId }),
    }),
  );

  return resp.json();
};

export const getNovaPreferences = async (userId: string): Promise<NovaPreferencesResponse> => {
  if (useMock) return mockGetNovaPreferences();

  const resp = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/${baseNovaUrl}/preferences${toQueryString({ userId })}`),
  );

  return resp.json();
};

export const saveNovaPreferences = async (
  aadGuid: string,
  preferences: NovaPreferences,
): Promise<{ succeeded: boolean }> => {
  if (useMock) return mockSaveNovaPreferences(preferences);

  const resp = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/${baseNovaUrl}/preferences?aadGuid=${aadGuid}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(preferences),
    }),
  );

  return resp.json();
};

// Saved images carry the presigned URL from when the answer was generated (~1 hour), so a
// chat reopened later has dead links. These re-sign them from each image's permanent `source`
// through the backend's image-url service. It needs no token of its own, and is reached via
// the /api proxy like every other Nova call (it has no CORS for direct browser calls).

// True once a presigned URL has expired, or is about to (so it doesn't die mid-view). Reads
// both link styles in play: SigV4 (`X-Amz-Date` + `X-Amz-Expires`, what the stream sends) and
// SigV2 (`Expires=<epoch seconds>`, what the image-url service returns). A URL whose expiry
// can't be read is treated as needing a refresh.
const needsFreshImageUrl = (url: string, skewMs = 5 * 60 * 1000): boolean => {
  try {
    const params = new URL(url).searchParams;

    const epochSeconds = Number(params.get('Expires'));
    if (epochSeconds) return Date.now() + skewMs >= epochSeconds * 1000;

    const signedAt = params.get('X-Amz-Date'); // 20261013T062224Z
    const lifetimeSeconds = Number(params.get('X-Amz-Expires'));
    const match = signedAt?.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
    if (!match || !lifetimeSeconds) return true;
    const [, year, month, day, hour, minute, second] = match.map(Number);
    const expiresAt = Date.UTC(year, month - 1, day, hour, minute, second) + lifetimeSeconds * 1000;
    return Date.now() + skewMs >= expiresAt;
  } catch {
    return true;
  }
};

const fetchFreshImageUrl = async (s3Uri: string): Promise<string> => {
  // Deliberately not through responseInterceptor: a 401 there redirects the whole page to
  // login, which is far too drastic for a background image refresh — a failure just drops
  // the image (see refreshNovaImageUrls).
  const resp = await fetch(`${await buildAPIUrl()}/${baseNovaUrl}/image-url${toQueryString({ s3_uri: s3Uri, expires_in: 3600 })}`, {
    signal: AbortSignal.timeout(8000),
  });
  if (!resp.ok) throw new Error(`Image URL request failed (${resp.status})`);

  // An object ({ success, s3_uri, url, expires_in, … }) — accept a bare string too.
  const body: unknown = await resp.json();
  const url = typeof body === 'string' ? body : (body as { url?: unknown } | null)?.url;
  if (typeof url !== 'string' || !/^https?:\/\//i.test(url)) throw new Error('Unexpected image URL response');
  return url;
};

const INLINE_IMAGE_PATTERN = /!\[([^\]]*)\]\(\s*<?(https?:\/\/[^\s)>]+)>?\s*\)|<img\b[^>]*?\bsrc=["'](https?:\/\/[^"']+)["'][^>]*>/gi;

const decodeHtmlAmp = (url: string): string => url.replace(/&amp;/g, '&');

const isSignedUrl = (url: string): boolean => {
  try {
    const params = new URL(url).searchParams;
    return params.has('X-Amz-Signature') || params.has('Signature');
  } catch {
    return false;
  }
};

const s3BucketOf = (s3Uri: string): string => s3Uri.match(/^s3:\/\/([^/]+)\//)?.[1] ?? '';

const s3UriFromPresignedUrl = (url: string): string => {
  try {
    const { hostname, pathname } = new URL(url);
    const path = decodeURIComponent(pathname.replace(/^\//, ''));
    const virtualHosted = hostname.match(/^(.+)\.s3[.-](?:[a-z0-9-]+\.)?amazonaws\.com$/i);
    if (virtualHosted && path) return `s3://${virtualHosted[1]}/${path}`;
    if (/^s3[.-](?:[a-z0-9-]+\.)?amazonaws\.com$/i.test(hostname) && path.includes('/')) return `s3://${path}`;
  } catch {
    return '';
  }
  return '';
};

const inlineImageUrls = (content: string): string[] =>
  Array.from(content.matchAll(INLINE_IMAGE_PATTERN), (match) => decodeHtmlAmp(match[2] ?? match[3] ?? '')).filter(
    Boolean,
  );

// Returns the messages with every expired image re-signed. An image whose URL can't be
// refreshed is dropped rather than left as a broken box. Each distinct `source` is requested
// once, in parallel.
export const refreshNovaImageUrls = async (messages: NovaChatMessage[]): Promise<NovaChatMessage[]> => {
  const allowedBuckets = new Set<string>();
  messages.forEach((message) =>
    message.images?.forEach((image) => {
      const bucket = s3BucketOf(image.source ?? '');
      if (bucket) allowedBuckets.add(bucket);
    }),
  );

  const sources = new Set<string>();
  const sourceByInlineUrl = new Map<string, string>();
  messages.forEach((message) => {
    message.images?.forEach((image) => {
      if (image.source && needsFreshImageUrl(image.url)) sources.add(image.source);
    });
    if (message.role !== 'assistant' || !message.content) return;
    inlineImageUrls(message.content).forEach((url) => {
      if (sourceByInlineUrl.has(url) || !isSignedUrl(url) || !needsFreshImageUrl(url)) return;
      const source =
        message.images?.find((image) => image.url === url && image.source)?.source ?? s3UriFromPresignedUrl(url);
      if (!allowedBuckets.has(s3BucketOf(source))) return;
      sourceByInlineUrl.set(url, source);
      sources.add(source);
    });
  });
  if (sources.size === 0) return messages;

  const fresh = new Map<string, string | null>();
  await Promise.all(
    Array.from(sources).map(async (source) => {
      try {
        fresh.set(source, await fetchFreshImageUrl(source));
      } catch (error) {
        console.error('Could not refresh Nova image URL:', error);
        fresh.set(source, null);
      }
    }),
  );

  const refreshContent = (content: string): string =>
    content.replace(INLINE_IMAGE_PATTERN, (tag, alt: string | undefined, markdownUrl?: string, htmlUrl?: string) => {
      const source = sourceByInlineUrl.get(decodeHtmlAmp(markdownUrl ?? htmlUrl ?? ''));
      if (!source || !fresh.has(source)) return tag;
      const url = fresh.get(source);
      if (!url) return '';
      if (markdownUrl !== undefined) return `![${alt ?? ''}](${url})`;
      return tag.replace(/(\bsrc=["'])[^"']+/i, (_match, prefix: string) => `${prefix}${url.replace(/&/g, '&amp;')}`);
    });

  return messages.map((message) => {
    const content =
      sourceByInlineUrl.size > 0 && message.role === 'assistant' && message.content
        ? refreshContent(message.content)
        : message.content;
    if (!message.images?.length) return content === message.content ? message : { ...message, content };
    const images = message.images.flatMap((image) => {
      if (!fresh.has(image.source)) return [image];
      const url = fresh.get(image.source);
      return url ? [{ ...image, url }] : [];
    });
    return { ...message, content, images };
  });
};
